import {Component, EventEmitter, Input, OnDestroy, OnInit, Output, Renderer2, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {EditorMmcModelService} from '@irb/irb-editors/editor-mmc-model/editor-mmc-model.service';
import {TooltipService} from '@irb-common/components/tooltip/tooltip.service';
import {Subscription} from 'rxjs';
import {UtilityService} from '@irb-common/services/utility.service';
import {IDropdownItem} from '@irb-common/models/dropdown.model';
import {DropdownFilterComponent} from "@irb-common/components/dropdown-filter/dropdown-filter.component";
import {IUserDetails} from '@irb/app.model';
import { AppService } from '@irb/app.service';

@Component({ standalone: false,
    selector: 'irb-mmc-overview-model',
    templateUrl: './mmc-overview-model.component.html',
    styleUrls: ['./mmc-overview-model.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager
})
export class MmcOverviewModelComponent implements OnInit, OnDestroy {

    @ViewChild('IrbDropdownAssetClass') IrbDropdownAssetClass: any;
    @ViewChild('IrbDropdownAllocChanges') IrbDropdownAllocChanges: any;
    @ViewChild('IrbDropdownRiskTolerance') IrbDropdownRiskTolerance: any;
    @ViewChild('psnid') psnid: any;
    @ViewChild('txtTurnover') txtTurnover: any;

    isTaxManaged: boolean;
    modelMarketName: string;
    assetClass: any;
    buildingBlockStyle: string;
    usedFor: string;
    turnover: number;
    allocationChangesSel: any;
    riskTolerance: any;
    isSociallyResponsible: boolean;
    morningStarId: any = null;
    sourceId: any = null;
    isValid: boolean = true;
    turnoverTooltipMessage: string = '';
    modelNameTooltipMessage: string;
    allocationChanges: string;
    msidIdTooltipMessage: string;
    private assetClassOptions: any = [];
    private riskToleranceOptions: any = [];
    private allocationChangesList: any = [];
    private ddSub: Subscription;
    userDetails: any = {};

    @Input() data: any;
    @Output() change = new EventEmitter<boolean>();
    @Output() validChange = new EventEmitter<any>();
    @Input() eventModelType: string;

    constructor(private editorMmcModelService: EditorMmcModelService, private tts: TooltipService,
                private util: UtilityService,
                private renderer: Renderer2,
                private as: AppService) {

        this.allocationChangesList = [
            {value: 'ANNUALLY', name: '+/- Annually'},
            {value: 'QUARTERLY', name: '+/- Quarterly'},
            {value: 'MONTHLY', name: '+/- Monthly'},
            {value: 'CONTINUOUS', name: 'Continuous/as needed'},
            {value: 'NEVER', name: 'Never'}
        ];

        this.getRoles(); //IREBAL-25075
    }

    ngOnInit(): void {
        this.initValues();
        this.getDropDownsInfo();
        this.ddSub = this.editorMmcModelService.dropDownsDataLoaded.subscribe(() => {
            this.getDropDownsInfo()
        })
    }


    private initValues() {
        if (this.data) {
            this.modelMarketName = this.data.modelMarketName;
            this.isTaxManaged = this.data.isTaxManaged;
            this.assetClass = {value: this.data.assetClass.id, name: this.data.assetClass.label};
            this.buildingBlockStyle = this.data.buildingBlockStyle === 'Smart Beta' ? 'Smart Beta' : this.data.buildingBlockStyle.toLowerCase();
            this.usedFor = this.data.usedFor.toLowerCase();
            this.turnover = this.data.turnover;
            this.allocationChanges = this.data.allocationChanges;
            this.getAllocChangesByValue(this.data.allocationChanges);
            this.riskTolerance = {value: this.data.riskTolerance.id, name: this.data.riskTolerance.label};
            this.isSociallyResponsible = this.data.isSociallyResponsible;
            this.morningStarId = this.data.morningStarId;
            this.sourceId = this.data.sourceId;

            this.validateEntries();
        } else {
            this.isValid = false;
            this.modelMarketName = '';
            this.allocationChanges = '';
            this.isTaxManaged = false;
            this.isSociallyResponsible = false;
        }
    }

    private getAllocChangesByValue(val: string) {
        const o: any = this.allocationChangesList.filter((a: any) => {
            return a.value === val;
        });
        if (o && o.length !== 0) {
            this.allocationChangesSel = o[0];
        }
    }

    private getDropDownsInfo() {
        const dd: any = this.editorMmcModelService.getDropDownItems();
        if (dd) {
            const sortByKey = this.util.sortByKey;
            this.assetClassOptions = this.convertToValueLabel(dd.ASSET_CLASS.items);
            this.assetClassOptions = sortByKey(this.assetClassOptions, "name", 'asc');
            this.riskToleranceOptions = this.convertToValueLabel(dd.RISK_TOLERANCE.items);
        }
    }

    getAssetClasses(searchStr: any) {
        this.IrbDropdownAssetClass.set(DropdownFilterComponent.getFilteredList(searchStr, this.assetClassOptions));
    }

    getAllocChanges(searchStr: string) {
        this.IrbDropdownAllocChanges.set(DropdownFilterComponent.getFilteredList(searchStr, this.allocationChangesList));
    }

    getRiskTolerances(searchStr: string) {
        this.IrbDropdownRiskTolerance.set(DropdownFilterComponent.getFilteredList(searchStr, this.riskToleranceOptions));
    }

    private convertToValueLabel(items: Array<any>) {
        let newArray: Array<any> = [], newEl: any;
        items.forEach((item: any) => {
            newEl = {
                value: item.id,
                name: item.label
            };
            newArray.push(newEl);
        });
        return newArray;
    }


    onTurnoverInput() {
        if (this.txtTurnover && this.txtTurnover.inputControl) {
            if (!this.txtTurnover.inputControl.valid && !this.data.isPaidForModel) {
                this.validChange.emit({type: 'overview', valid: false});
                return;
            }
            this.validateEntries();
        }
    }

    onPsnIdInput() {
        if (!this.psnid.inputControl.valid && !this.data.isPaidForModel) {
            this.validChange.emit({type: 'overview', valid: false});
            return;
        }
        this.validateEntries();
    }

    onAssetClassChange(newAsset: any) {
        if (this.data && this.eventModelType === 'editMmcModel' && this.data.assetType) {
            this.data.assetClass.id = newAsset.value;
            this.data.assetClass.label = newAsset.name;
            this.data.assetClass.isUpdated = true;
        } else {
            this.data.assetClass = {
                id: newAsset.value,
                label: newAsset.name,
                isNew: true,
                isUIdeleted: false
            }
        }
        this.assetClass = newAsset;

        this.validateEntries();
    }

    onAllocChngChange(newAllocCh: any) {
        this.allocationChanges = newAllocCh.value;
        this.validateEntries();
    }

    onRiskCategoryChange(newRiskTolerance: any) {
        if (this.data && this.eventModelType === 'editMmcModel' && this.data.riskTolerance) {
            this.data.riskTolerance.id = newRiskTolerance.value;
            this.data.riskTolerance.label = newRiskTolerance.name;
            this.data.riskTolerance.isUpdated = true;
        } else {
            this.data.riskTolerance = {
                id: newRiskTolerance.value,
                label: newRiskTolerance.name,
                isNew: true,
                isUIdeleted: false
            }
        }
        this.riskTolerance = newRiskTolerance;
        this.validateEntries();
    }

    onMorningstarChange() {
        if (this.morningStarId.length === 25) {
            this.msidIdTooltipMessage = 'Max character limit is 25';
        } else {
            this.msidIdTooltipMessage = '';
            this.tts.remove();
            this.validateEntries();
        }
    }

    validateModelName() {
        setTimeout(() => {
            if (this.modelMarketName.length === 100) {
                this.modelNameTooltipMessage = 'Max character limit is 100';
            } else {
                this.modelNameTooltipMessage = '';
                this.tts.remove();
                this.validateEntries();
            }
        });
    }

    validateEntries() {
        if (this.morningStarId !== null) {
            if (this.morningStarId.toString().trim() === '') {
                this.morningStarId = null;
            }
        }
        if (this.sourceId !== null) {
            if (this.sourceId.toString().trim() === '') {
                this.sourceId = null;
            }

        }

        this.isValid = this.modelMarketName.trim() === ''
        || typeof this.isTaxManaged === 'undefined'
        || typeof this.assetClass === 'undefined'
        || typeof this.buildingBlockStyle === 'undefined'
        || typeof this.usedFor === 'undefined'
        || typeof this.allocationChanges === 'undefined'
        || typeof this.riskTolerance === 'undefined'
        || typeof this.isSociallyResponsible === 'undefined'
        || this.turnoverTooltipMessage !== ''
        || (!this.morningStarId && !this.sourceId)
            ? false : true;

        this.updateDataValues();
        this.change.emit();
        this.validChange.emit({type: 'overview', valid: this.isValid});
    }

    private updateDataValues() {
        this.data.modelMarketName = this.modelMarketName;
        this.data.isTaxManaged = this.isTaxManaged;

        this.data.buildingBlockStyle = this.buildingBlockStyle;
        this.data.usedFor = this.usedFor;
        this.data.turnover = this.turnover ? this.turnover : null;
        this.data.allocationChanges = this.allocationChanges;
        //this.riskTolerance = this.data.riskTolerance.id;
        this.data.isSociallyResponsible = this.isSociallyResponsible;
        this.data.morningStarId = this.morningStarId;
        this.data.sourceId = this.sourceId;
    }

    private getRoles() {
        this.userDetails = this.as.getItem('IRB_APP_CONFIG') as IUserDetails || {};
    }

    ngOnDestroy() {
        if (this.ddSub) {
            this.ddSub.unsubscribe();
        }
    }

}
